import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Printer, PackageOpen, Loader2, Download, Check, Plus, Minus, Save, Edit } from 'lucide-react';
import { packingListsAPI, equipmentAPI, customPackingItemsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import html2pdf from 'html2pdf.js';

const PackingList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contract = location.state?.contract;
  const fromBEO = location.state?.fromBEO || false;
  const [selectedEquipment, setSelectedEquipment] = useState({});
  const [allEquipment, setAllEquipment] = useState([]);
  const [customItems, setCustomItems] = useState([]);  // [{id, name, quantity, category}]
  const [savedSuggestions, setSavedSuggestions] = useState([]);  // library, sorted by usage desc
  const [librarySearch, setLibrarySearch] = useState('');
  const [activeSuggestionRow, setActiveSuggestionRow] = useState(null);  // id of row showing dropdown
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedPackingList, setSavedPackingList] = useState(null);
  const [viewMode, setViewMode] = useState('select'); // 'select' or 'view'
  const contentRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!contract) {
      navigate('/contracts');
      return;
    }
    fetchData();
  }, [contract, navigate]);

  const fetchData = async () => {
    try {
      const [equipmentData, suggestionsData, existingLists] = await Promise.all([
        equipmentAPI.getAll(),
        customPackingItemsAPI.list(null, 200).catch(() => []),
        packingListsAPI.getForContract(contract.id).catch(() => [])
      ]);

      setAllEquipment(equipmentData);
      setSavedSuggestions(suggestionsData || []);

      // Rehydrate from an existing packing list (most recent), so coming back
      // to a contract's packing list does NOT lose previously saved items.
      // Source of truth = the packing list itself. Never pull from the contract.
      const existing = (existingLists || []).sort(
        (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
      )[0];

      if (existing) {
        setSavedPackingList(existing);
        // Rebuild equipment selections from saved items whose id matches a known equipment id
        const equipmentIds = new Set(equipmentData.map(e => e.id));
        const restoredEquipment = {};
        const restoredCustoms = [];
        (existing.items || []).forEach(it => {
          if (equipmentIds.has(it.id)) {
            restoredEquipment[it.id] = { selected: true, quantity: it.quantity || 1 };
          } else if (it.category === 'custom' || String(it.id || '').startsWith('custom-') || String(it.id || '').startsWith('lib-')) {
            restoredCustoms.push({
              id: it.id || `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              name: it.name || '',
              quantity: it.quantity || 1,
              category: 'custom',
            });
          }
          // Any legacy auto-generated items (food_menus / food_items / bar_supplies / on_the_turn)
          // are intentionally discarded — user requested no contract-driven items, ever.
        });
        setSelectedEquipment(restoredEquipment);
        setCustomItems(restoredCustoms);
      } else {
        // Fresh list: pre-select equipment marked as "required for all offsite"
        const preSelected = {};
        equipmentData.forEach(item => {
          if (item.required_for?.includes('all_offsite') && contract.location_type === 'offsite') {
            preSelected[item.id] = { selected: true, quantity: item.quantity || 1 };
          }
        });
        setSelectedEquipment(preSelected);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({ title: 'Error loading data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const toggleEquipment = (itemId, defaultQty = 1) => {
    setSelectedEquipment(prev => {
      if (prev[itemId]?.selected) {
        const { [itemId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: { selected: true, quantity: defaultQty } };
    });
  };

  const updateQuantity = (itemId, qty) => {
    if (qty < 1) return;
    setSelectedEquipment(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: qty }
    }));
  };

  // Custom items: write-in name + quantity
  const addCustomItem = () => {
    setCustomItems(prev => [
      ...prev,
      { id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: '', quantity: 1, category: 'custom' }
    ]);
  };
  const updateCustomItem = (id, patch) => {
    setCustomItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };
  const removeCustomItem = (id) => {
    setCustomItems(prev => prev.filter(it => it.id !== id));
  };
  const getValidCustomItems = () =>
    customItems.filter(it => (it.name || '').trim().length > 0);

  // Library helpers: check/uncheck a saved item to include it on THIS packing list
  const isLibraryItemSelected = (libItem) => {
    const target = (libItem.normalized_name || libItem.name?.toLowerCase() || '').trim();
    return customItems.some(c => (c.name || '').trim().toLowerCase() === target);
  };
  const toggleLibraryItem = (libItem) => {
    const target = (libItem.normalized_name || libItem.name?.toLowerCase() || '').trim();
    setCustomItems(prev => {
      const existingIdx = prev.findIndex(c => (c.name || '').trim().toLowerCase() === target);
      if (existingIdx >= 0) {
        return prev.filter((_, i) => i !== existingIdx);
      }
      return [
        ...prev,
        {
          id: `lib-${libItem.id || Date.now()}`,
          name: libItem.name,
          quantity: libItem.last_quantity || 1,
          category: 'custom',
        }
      ];
    });
  };
  const deleteLibraryItem = async (libItem) => {
    if (!window.confirm(`Permanently remove "${libItem.name}" from the saved library?`)) return;
    try {
      await customPackingItemsAPI.remove(libItem.id);
      setSavedSuggestions(prev => prev.filter(s => s.id !== libItem.id));
      // Also un-check it from current selection if it was selected
      const target = (libItem.normalized_name || libItem.name?.toLowerCase() || '').trim();
      setCustomItems(prev => prev.filter(c => (c.name || '').trim().toLowerCase() !== target));
      toast({ title: 'Removed from library' });
    } catch (e) {
      toast({ title: 'Could not remove', variant: 'destructive' });
    }
  };

  // Autocomplete: filter saved suggestions by current input prefix (case-insensitive)
  // Excludes items already in the current customItems list to prevent duplicates.
  const getFilteredSuggestions = (typed) => {
    const q = (typed || '').trim().toLowerCase();
    if (!q || savedSuggestions.length === 0) return [];
    const alreadyAdded = new Set(
      customItems
        .map(it => (it.name || '').trim().toLowerCase())
        .filter(Boolean)
    );
    return savedSuggestions
      .filter(s => (s.normalized_name || s.name?.toLowerCase() || '').includes(q))
      .filter(s => !alreadyAdded.has((s.name || '').trim().toLowerCase()))
      .slice(0, 6);
  };
  const applySuggestion = (rowId, suggestion) => {
    updateCustomItem(rowId, {
      name: suggestion.name,
      quantity: suggestion.last_quantity || 1
    });
    setActiveSuggestionRow(null);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = contentRef.current;
    const opt = {
      margin: 0.5,
      filename: `PackingList_${contract.contract_number || contract.event_name}_${contract.event_date}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
    toast({ title: 'PDF Downloaded!' });
  };

  const handleGenerateList = async () => {
    // Save packing list to database
    setSaving(true);
    try {
      const items = getSelectedItems().map(item => ({
        id: item.id,
        name: item.name,
        category: item.category,
        quantity: item.quantity,
        checked: false
      }));

      // Custom write-in items (the ONLY non-equipment items — nothing is pulled from the contract)
      const customs = getValidCustomItems().map(it => ({
        id: it.id,
        name: it.name.trim(),
        category: it.category || 'custom',
        quantity: parseInt(it.quantity) || 1,
        checked: false
      }));

      const packingListData = {
        contract_id: contract.id,
        contract_number: contract.contract_number || '',
        event_name: contract.event_name,
        event_date: contract.event_date,
        client_name: contract.client_name,
        guest_count: contract.guest_count,
        location: contract.location,
        items: [...items, ...customs]
      };

      // Update if we already have a saved list for this contract; otherwise create
      const saved = savedPackingList?.id
        ? await packingListsAPI.update(savedPackingList.id, packingListData)
        : await packingListsAPI.create(packingListData);
      setSavedPackingList(saved);

      // Auto-save custom items to the saved-items library (best effort, parallel)
      if (customs.length > 0) {
        await Promise.all(customs.map(c =>
          customPackingItemsAPI.upsert({ name: c.name, quantity: c.quantity }).catch(() => null)
        ));
        // Refresh suggestions so the list grows over time
        customPackingItemsAPI.list(null, 200).then(setSavedSuggestions).catch(() => {});
      }

      toast({ title: 'Packing list saved!', description: `${items.length} equipment + ${customs.length} custom items.` });
    } catch (error) {
      console.error('Error saving packing list:', error);
      toast({ title: 'Note: List generated but not saved', description: 'View mode active', variant: 'default' });
    } finally {
      setSaving(false);
    }

    setViewMode('view');
  };

  // Get selected items with their details
  const getSelectedItems = () => {
    return allEquipment.filter(item => selectedEquipment[item.id]?.selected).map(item => ({
      ...item,
      quantity: selectedEquipment[item.id]?.quantity || 1
    }));
  };

  // Group selected equipment by category
  const getGroupedSelectedItems = () => {
    const items = getSelectedItems();
    return items.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  };

  const categoryLabels = {
    'general_offsite': 'General Offsite Items',
    'food_menus': 'Menus',
    'food_items': 'Food & Ingredients',
    'bar_supplies': 'Bar Supplies',
    'on_the_turn': 'On the Turn',
    'serving': 'Serving Equipment',
    'dishware': 'Dishware & Flatware',
    'transport': 'Transport Equipment',
    'setup': 'Setup Items',
    'beer': 'Beer',
    'wine': 'Wine',
    'coolers': 'Coolers',
    'liquor': 'Liquor',
    'mixers': 'Mixers',
    'garnish': 'Garnish',
    'custom': 'Custom Items'
  };

  // Get label for category - use custom label if not in defaults
  const getCategoryLabel = (category) => {
    return categoryLabels[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const unitLabels = {
    'each': 'ea',
    'sleeve': 'sleeve',
    'case': 'case',
    'box': 'box',
    'pack': 'pack',
    'set': 'set'
  };

  const getSelectedCount = () => {
    return Object.values(selectedEquipment).filter(v => v.selected).length + getValidCustomItems().length;
  };

  // Group selected equipment + custom items by category for view mode
  const getGroupedAllSelected = () => {
    const grouped = getGroupedSelectedItems();
    const customs = getValidCustomItems();
    if (customs.length > 0) {
      grouped['custom'] = customs.map(it => ({
        ...it,
        name: it.name.trim(),
        quantity: parseInt(it.quantity) || 1
      }));
    }
    return grouped;
  };

  // Group all equipment by category for selection view
  const groupedEquipment = allEquipment.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  // VIEW MODE - Shows only selected items in a clean printable format
  if (viewMode === 'view') {
    const groupedSelected = getGroupedAllSelected();
    
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen print:bg-white print:p-0" data-testid="packing-list-view">
        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-8 print:hidden">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setViewMode('select')}
              variant="outline"
              className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
            >
              <Edit size={20} className="mr-2" />
              Edit Selection
            </Button>
            {savedPackingList && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <Check size={16} /> Saved
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={handleDownloadPDF} className="bg-[#8b7355] hover:bg-[#c4a574] text-white">
              <Download size={20} className="mr-2" />Download PDF
            </Button>
            <Button onClick={handlePrint} className="bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
              <Printer size={20} className="mr-2" />Print
            </Button>
          </div>
        </div>

        <div ref={contentRef}>
          <Card className="border-none shadow-lg print:shadow-none">
            <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white print:bg-white print:text-[#1e3a5f] print:border-b-4 print:border-[#8b7355]">
              <div className="text-center">
                <CardTitle className="text-3xl mb-2 flex items-center justify-center space-x-3">
                  <PackageOpen size={32} />
                  <span>PACKING LIST</span>
                </CardTitle>
                <p className="text-lg opacity-90 print:text-gray-600">
                  {contract.contract_number} - {contract.event_name}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {/* Event Details */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#f5f5dc] to-white rounded-lg border-2 border-[#c9a227]">
                <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">EVENT DETAILS</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Event Name</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{contract.event_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Date</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">
                      {(() => { const [y,m,d] = (contract.event_date||'').split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Time</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{formatTime(contract.event_time) || contract.event_time}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Guest Count</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{contract.guest_count} guests</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-gray-600 mb-1">Location</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{contract.location}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Client</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{contract.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Phone</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{contract.client_phone}</p>
                  </div>
                </div>
              </div>

              {/* Selected Equipment - Grouped by Category */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4 border-b-2 border-[#c9a227] pb-2">
                  EQUIPMENT & SUPPLIES ({getSelectedCount()} items)
                </h2>
                
                {Object.entries(groupedSelected).map(([category, items]) => (
                  <div key={category} className="mb-6">
                    <h3 className="font-bold text-[#8b7355] mb-3">{getCategoryLabel(category)}</h3>
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="w-10 p-3 text-center">✓</th>
                            <th className="text-left p-3">Item</th>
                            <th className="w-20 text-center p-3">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id} className="border-t">
                              <td className="p-3 text-center">
                                <div className="w-5 h-5 border-2 border-gray-400 rounded mx-auto"></div>
                              </td>
                              <td className="p-3">
                                <p className="font-semibold text-[#1e3a5f]">{item.name}</p>
                                {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                              </td>
                              <td className="p-3 text-center font-bold text-[#1e3a5f]">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              {/* Loading Instructions */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded mb-8">
                <h3 className="font-bold text-[#1e3a5f] mb-3 text-lg">Loading Instructions</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Load cold items last to maintain temperature</li>
                  <li>• Keep raw and cooked items separate</li>
                  <li>• Ensure all containers are properly sealed</li>
                  <li>• Pack fragile items with proper cushioning</li>
                </ul>
              </div>

              {/* Signature Section */}
              <div className="border-t-2 border-gray-300 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">Packed By:</p>
                    <div className="border-b-2 border-gray-400 h-8"></div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">Date/Time:</p>
                    <div className="border-b-2 border-gray-400 h-8"></div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">Verified By:</p>
                    <div className="border-b-2 border-gray-400 h-8"></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // SELECT MODE - Shows all items with checkboxes for selection
  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="packing-list-select">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-8">
        <Button
          onClick={() => navigate(fromBEO ? '/beo' : '/contracts', { state: { contract } })}
          variant="outline"
          className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
        >
          <ArrowLeft size={20} className="mr-2" />
          {fromBEO ? 'Back to BEO' : 'Back to Contracts'}
        </Button>
        <Button
          onClick={handleGenerateList}
          disabled={getSelectedCount() === 0 || saving}
          className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
          data-testid="generate-list-btn"
        >
          {saving ? (
            <>
              <Loader2 size={20} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={20} className="mr-2" />
              Generate & Save Packing List ({getSelectedCount()} items)
            </>
          )}
        </Button>
      </div>

      <Card className="border-none shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white">
          <div className="text-center">
            <CardTitle className="text-2xl mb-2">Select Items for Packing List</CardTitle>
            <p className="opacity-90">{contract.event_name} - {(() => { const [y,m,d] = (contract.event_date||'').split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString(); })()}</p>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Quick Stats */}
          <div className="bg-[#1e3a5f] text-white p-4 rounded-lg mb-6 flex justify-between items-center">
            <div>
              <p className="text-sm opacity-80">Event</p>
              <p className="font-bold">{contract.event_name}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Guests</p>
              <p className="font-bold">{contract.guest_count}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Location</p>
              <p className="font-bold">{contract.location_type}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Selected Items</p>
              <p className="font-bold text-2xl">{getSelectedCount()}</p>
            </div>
          </div>

          {/* Custom Items - Write-in items with quantities */}
          <div className="mb-6 bg-white border-2 border-[#c9a227] rounded-lg overflow-hidden">
            <div className="bg-[#c9a227] text-[#1e3a5f] p-3 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2">
                <Edit size={18} />
                Custom Items <span className="text-xs font-normal opacity-70">(write-in)</span>
              </h3>
              <Button
                type="button"
                onClick={addCustomItem}
                size="sm"
                className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
                data-testid="add-custom-item-btn"
              >
                <Plus size={16} className="mr-1" />Add Item
              </Button>
            </div>
            <div className="p-4">
              {/* Saved library — tick to include on THIS packing list */}
              {savedSuggestions.length > 0 && (() => {
                // Filter by search query (case-insensitive substring) and sort alphabetically
                const q = librarySearch.trim().toLowerCase();
                const filteredLibrary = savedSuggestions
                  .filter(lib => !q || (lib.name || '').toLowerCase().includes(q))
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                return (
                <div className="mb-4 pb-4 border-b" data-testid="custom-items-library">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-[#1e3a5f] uppercase tracking-wide">
                      Saved Library ({filteredLibrary.length}{q ? ` of ${savedSuggestions.length}` : ''})
                    </p>
                    <p className="text-xs text-gray-500 italic">Tick to add to this list</p>
                  </div>
                  {/* Search input */}
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={librarySearch}
                      onChange={(e) => setLibrarySearch(e.target.value)}
                      placeholder="Search saved items…"
                      className="w-full px-3 py-2 pr-8 text-sm border-2 border-gray-200 rounded focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="library-search-input"
                    />
                    {librarySearch && (
                      <button
                        type="button"
                        onClick={() => setLibrarySearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none"
                        aria-label="Clear search"
                        data-testid="library-search-clear"
                      >×</button>
                    )}
                  </div>
                  {filteredLibrary.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-2" data-testid="library-empty-hint">No saved items match &ldquo;{librarySearch}&rdquo;.</p>
                  ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {filteredLibrary.map((lib) => {
                      const checked = isLibraryItemSelected(lib);
                      return (
                        <label
                          key={lib.id}
                          className={`flex items-center gap-2 px-3 py-2 rounded border-2 cursor-pointer transition-colors ${
                            checked ? 'border-[#1e3a5f] bg-green-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`library-item-${lib.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLibraryItem(lib)}
                            className="w-4 h-4"
                            data-testid={`library-item-checkbox-${lib.id}`}
                          />
                          <span className="flex-1 text-sm font-medium text-[#1e3a5f] truncate" title={lib.name}>
                            {lib.name}
                          </span>
                          <span className="text-xs text-gray-500">×{lib.last_quantity || 1}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteLibraryItem(lib); }}
                            className="text-xs text-red-500 hover:text-red-700 px-1"
                            title="Remove from saved library"
                            data-testid={`library-item-delete-${lib.id}`}
                          >
                            ×
                          </button>
                        </label>
                      );
                    })}
                  </div>
                  )}
                </div>
                );
              })()}

              {customItems.length === 0 ? (
                <p className="text-sm text-gray-500 italic text-center py-3">
                  {savedSuggestions.length > 0
                    ? 'Tick saved items above, or click "Add Item" to write in something new.'
                    : 'Click "Add Item" to write in custom items (e.g., "Extra ice — 4 bags"). Saved items appear here next time.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {customItems.map((it, idx) => {
                    const suggestions = activeSuggestionRow === it.id ? getFilteredSuggestions(it.name) : [];
                    return (
                    <div key={it.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`custom-item-row-${idx}`}>
                      <div className="col-span-7 relative">
                        <Input
                          type="text"
                          placeholder="Item name (e.g., Extra Sterno fuel)"
                          value={it.name}
                          onChange={(e) => updateCustomItem(it.id, { name: e.target.value })}
                          onFocus={() => setActiveSuggestionRow(it.id)}
                          onBlur={() => setTimeout(() => setActiveSuggestionRow(prev => prev === it.id ? null : prev), 150)}
                          autoComplete="off"
                          data-testid={`custom-item-name-${idx}`}
                        />
                        {suggestions.length > 0 && (
                          <div
                            className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                            data-testid={`custom-item-suggestions-${idx}`}
                          >
                            {suggestions.map((s, sIdx) => (
                              <button
                                type="button"
                                key={s.id}
                                onMouseDown={(e) => { e.preventDefault(); applySuggestion(it.id, s); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-[#f5f5dc] flex justify-between items-center border-b last:border-b-0"
                                data-testid={`custom-item-suggestion-${idx}-${sIdx}`}
                              >
                                <span className="font-medium text-[#1e3a5f]">{s.name}</span>
                                <span className="text-xs text-gray-500">
                                  qty {s.last_quantity} · used {s.usage_count}×
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="col-span-3 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => updateCustomItem(it.id, { quantity: Math.max(1, (parseInt(it.quantity) || 1) - 1) })}
                          className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                        >
                          <Minus size={12} />
                        </button>
                        <Input
                          type="number"
                          min="1"
                          value={it.quantity}
                          onChange={(e) => updateCustomItem(it.id, { quantity: parseInt(e.target.value) || 1 })}
                          className="text-center"
                          data-testid={`custom-item-qty-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => updateCustomItem(it.id, { quantity: (parseInt(it.quantity) || 1) + 1 })}
                          className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <Button
                        type="button"
                        onClick={() => removeCustomItem(it.id)}
                        size="sm"
                        variant="ghost"
                        className="col-span-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`custom-item-remove-${idx}`}
                      >
                        Remove
                      </Button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Equipment Selection */}
          <div className="space-y-6">
            {Object.entries(groupedEquipment).map(([category, items]) => (
              <div key={category} className="bg-white border rounded-lg overflow-hidden">
                <div className="bg-[#8b7355] text-white p-3 flex justify-between items-center">
                  <h3 className="font-bold">{getCategoryLabel(category)}</h3>
                  <span className="text-sm opacity-80">
                    {items.filter(i => selectedEquipment[i.id]?.selected).length} / {items.length} selected
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((item) => {
                      const isSelected = selectedEquipment[item.id]?.selected;
                      const qty = selectedEquipment[item.id]?.quantity || item.quantity || 1;
                      const isSupply = item.item_type === 'supply';
                      const unitLabel = unitLabels[item.unit_type] || 'ea';
                      
                      return (
                        <div
                          key={item.id}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            isSelected ? 'border-[#1e3a5f] bg-green-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              type="button"
                              onClick={() => toggleEquipment(item.id, item.quantity || 1)}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-[#1e3a5f] border-[#1e3a5f] text-white' : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <Check size={14} />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-[#1e3a5f]">{item.name}</p>
                              {item.description && <p className="text-xs text-gray-500 truncate">{item.description}</p>}
                              {isSupply && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  Per {unitLabel}
                                </span>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="mt-2 flex items-center gap-2 ml-9">
                              <span className="text-xs text-gray-600">{isSupply ? `${unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}s:` : 'Qty:'}</span>
                              <button onClick={() => updateQuantity(item.id, qty - 1)} className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center">
                                <Minus size={12} />
                              </button>
                              <Input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                className="w-14 h-6 text-center text-sm p-1"
                              />
                              <button onClick={() => updateQuantity(item.id, qty + 1)} className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center">
                                <Plus size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PackingList;
